import * as React from 'react'
import { View, FlatList, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import TextInputComponent from '@components/textInput/TextInput'
import SortSelector from '@components/modal/SortSelector'
import { RadioOption } from '@components/radio/Radio'
import Modal from '@components/modal/Modal'
import Loader from '@components/loader/Loader'
import SimpleAlert from '@components/modal/SimpleAlert'

import ServiceCard from '@components/card/ServiceCard'

import { filterSocietes } from '@utils/admin'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'
import * as AuthStore from '@store/auth/auth'

import api from '@api/api'
import Logger from '@utils/logger'
import exporter from '@utils/exporter'
import config from '@app/config'

const AdminPrestaListScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  // keep only enabled
  const societes = useReducer(AdminStore.store, (s) => s.services)
  const services = societes.filter((s) => s.enabled)

  const [status, setStatus] = React.useState<'fetched' | 'loading'>('loading')
  const [exporting, setExporting] = React.useState(false)
  const [search, setSearch] = React.useState('')

  const sorts: RadioOption[] = [{ label: i18n.t('screens.admin.users.sorts.name'), value: 'name' }]

  const [reverse, setReverse] = React.useState(false)
  const [sort, setSort] = React.useState<RadioOption | undefined>(sorts[0])

  React.useEffect(() => {
    api.societe
      .all()
      .then(AdminStore.actions.setServices)
      .catch(Logger.error)
      .finally(() => setStatus('fetched'))
  }, [])

  React.useEffect(() => {
    if (exporting) {
      const { tokens } = AuthStore.store.getState()

      if (tokens) {
        if (isPhone) {
          Modal.open({ Content: () => <Loader />, position: 'center' })
        }
        exporter
          .download<SocieteExportRequest>(config.ws.societe.export, `export_societe.xlsx`, 'application/vnd.ms-excel', {
            societeIds: displayedData.map((data) => data.id),
          })
          .then(() => {
            if (isPhone) {
              SimpleAlert.open({
                title: i18n.t('screens.admin.providers.export.success.title'),
                description: i18n.t('screens.admin.providers.export.success.description'),
                buttons: [
                  { label: i18n.t('screens.admin.providers.export.success.ok'), onClick: () => SimpleAlert.close() },
                ],
              })
            }
          })
          .catch((err) => {
            Logger.error('Unable to download ', err)
            SimpleAlert.open({
              title: i18n.t('screens.admin.providers.export.error.title'),
              description: i18n.t('screens.admin.providers.export.error.description'),
              buttons: [
                { label: i18n.t('screens.admin.providers.export.error.ok'), onClick: () => SimpleAlert.close() },
              ],
            })
          })
          .finally(() => {
            if (isPhone) {
              Modal.close()
            }
            setExporting(false)
          })
      }
    }
  }, [exporting])

  const displayedData = React.useMemo(() => {
    const filtered = filterSocietes(services, search)

    const sorted = sort
      ? filtered.sort(
          (a, b) => (sort.value === 'name' ? a.nameSociete.localeCompare(b.nameSociete) : 1) * (reverse ? -1 : 1)
        )
      : filtered

    return sorted
  }, [services, sort, reverse, search])

  const exportButton: HeaderButton = {
    label: i18n.t('screens.admin.users.buttons.export'),
    onClick: () => setExporting(true),
    color: 'light',
    status: displayedData.length === 0 ? 'disabled' : exporting ? 'loading' : 'active',
  }

  const displayExport = true // TODO : intégrer les scopes

  const headerButtons: HeaderButton[] = displayExport ? [exportButton] : []

  const renderServiceCard = ({ item }: { item: SocieteResponse }) => (
    <ServiceContainer activeOpacity={0.9} onPress={() => navigation.push(`/admin/providers/${item.id}`)}>
      <ServiceCard service={item} />
    </ServiceContainer>
  )

  const renderSearch = () => (
    <SearchContainer>
      <TextInputComponent
        onChange={setSearch}
        searchStyle="onDark"
        placeholder={i18n.t('screens.incidentList.search')}
        left={{ name: 'search' }}
        value={search}
      />
    </SearchContainer>
  )

  return (
    <Root
      loading={status === 'loading'}
      header={isPhone ? i18n.t('screens.admin.providers.title') : undefined}
      renderSubHeader={isPhone ? renderSearch : undefined}
      fixed={isPhone}
      right="download"
      onRight={exportButton.onClick}
      onLeft={navigation.goBack}>
      {!isPhone && (
        <HeaderContainer>
          <Header title={i18n.t('screens.admin.providers.title')} buttons={headerButtons} renderBottom={renderSearch} />
        </HeaderContainer>
      )}
      <SortContainer>
        <SortSelector
          onOrderChange={() => setReverse(!reverse)}
          onSortChange={setSort}
          reverse={reverse}
          sorts={sorts}
          sort={sort}
        />
      </SortContainer>
      <FlatList
        data={displayedData}
        renderItem={renderServiceCard}
        contentContainerStyle={{
          flexWrap: isPhone ? undefined : 'wrap',
          flexDirection: isPhone ? 'column' : 'row',
          marginVertical: 20,
          marginHorizontal: isPhone ? 16 : 25,
          justifyContent: isPhone ? 'center' : 'flex-start',
        }}
        ListEmptyComponent={<MissingInfo>{i18n.t('screens.admin.users.empty')}</MissingInfo>}
        showsVerticalScrollIndicator={false}
      />
    </Root>
  )
}

export default AdminPrestaListScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const ServiceContainer = styled(TouchableOpacity)`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin: 4px;` : `margin-right: 16px;`)};
`

const SortContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint !== 'phone' ? `16px 0 0 24px` : `10px 16px`)};
`

const SearchContainer = styled(View)`
  margin-top: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
`
